import { useState, useRef, useEffect } from "react";
import type {
  EquipmentType,
  ExerciseEntry,
} from "@vlab-corporation/vlab-pilates-service-interface/class";
import type { Movement } from "@vlab-corporation/vlab-pilates-service-interface/movement";
import { EQUIPMENT_MOVEMENTS } from "@/constants/equipment-movements";

const EQUIPMENT_OPTIONS: { value: EquipmentType; label: string }[] = [
  { value: "reformer", label: "리포머" },
  { value: "cadillac", label: "캐딜락" },
  { value: "chair", label: "체어" },
  { value: "barrel", label: "배럴" },
  { value: "mat", label: "매트" },
];

/**
 * 기존 journal 데이터에서 equipment가 없는 exercises를 자동 매칭한다.
 * EQUIPMENT_MOVEMENTS에서 이름으로 검색하여 해당 기구를 할당.
 * 매칭 안 되면 첫 번째 선택 기구에 할당.
 */
export function migrateExercisesWithEquipment(
  exercises: ExerciseEntry[],
  equipments: EquipmentType[],
): ExerciseEntry[] {
  if (exercises.every((e) => e.equipment)) return exercises;
  if (equipments.length === 0) return exercises;

  return exercises.map((e) => {
    if (e.equipment) return e;
    // EQUIPMENT_MOVEMENTS에서 이름으로 기구 찾기
    for (const eq of equipments) {
      if ((EQUIPMENT_MOVEMENTS[eq] ?? []).includes(e.name)) {
        return { ...e, equipment: eq };
      }
    }
    // 매칭 실패 → 첫 번째 기구에 할당
    return { ...e, equipment: equipments[0] };
  });
}

interface EquipmentExerciseSectionProps {
  equipments: EquipmentType[];
  exercises: ExerciseEntry[];
  allMovements: Movement[];
  onEquipmentsChange: (equipments: EquipmentType[]) => void;
  onExercisesChange: (exercises: ExerciseEntry[]) => void;
}

export function EquipmentExerciseSection({
  equipments,
  exercises,
  allMovements,
  onEquipmentsChange,
  onExercisesChange,
}: EquipmentExerciseSectionProps) {
  function toggleEquipment(eq: EquipmentType) {
    if (equipments.includes(eq)) {
      const exercisesForEquipment = exercises.filter((e) => e.equipment === eq);
      if (exercisesForEquipment.length > 0) {
        const confirmed = window.confirm(
          `${EQUIPMENT_OPTIONS.find((o) => o.value === eq)?.label} 동작 ${exercisesForEquipment.length}개가 삭제됩니다. 계속하시겠습니까?`,
        );
        if (!confirmed) return;
        onExercisesChange(exercises.filter((e) => e.equipment !== eq));
      }
      onEquipmentsChange(equipments.filter((e) => e !== eq));
    } else {
      onEquipmentsChange([...equipments, eq]);
    }
  }

  function addExercise(equipment: EquipmentType, name: string, movementId?: string) {
    const alreadyAdded = exercises.some((e) => e.name === name && e.equipment === equipment);
    if (alreadyAdded) return;
    onExercisesChange([...exercises, { name, equipment, ...(movementId ? { movementId } : {}) }]);
  }

  function removeExercise(index: number) {
    onExercisesChange(exercises.filter((_, i) => i !== index));
  }

  function updateExerciseNote(index: number, notes: string) {
    onExercisesChange(
      exercises.map((e, i) => (i === index ? { ...e, notes: notes || undefined } : e)),
    );
  }

  function getExercisesForEquipment(
    eq: EquipmentType,
  ): { exercise: ExerciseEntry; globalIndex: number }[] {
    return exercises
      .map((exercise, globalIndex) => ({ exercise, globalIndex }))
      .filter(({ exercise }) => exercise.equipment === eq);
  }

  return (
    <div id="section-equipment-exercises" className="rounded-lg bg-white shadow-sm p-4 mb-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        기구 & 수업 시퀀스 <span className="text-red-500">*</span>
      </h3>

      {/* Equipment chips */}
      <div className="flex gap-2 flex-wrap mb-3">
        {EQUIPMENT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => toggleEquipment(option.value)}
            className={[
              "px-4 py-2 rounded-full text-sm font-medium border transition-colors",
              equipments.includes(option.value)
                ? "bg-primary-600 text-white border-primary-600"
                : "bg-white text-gray-700 border-gray-300 hover:border-primary-400",
            ].join(" ")}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Per-equipment accordion */}
      {equipments.length === 0 && (
        <p className="text-sm text-gray-400 mt-2">기구를 선택하면 동작을 추가할 수 있습니다.</p>
      )}

      <div className="space-y-3">
        {equipments.map((eq) => (
          <EquipmentAccordion
            key={eq}
            equipment={eq}
            label={EQUIPMENT_OPTIONS.find((o) => o.value === eq)?.label ?? eq}
            exerciseItems={getExercisesForEquipment(eq)}
            allMovements={allMovements}
            onAdd={(name, movementId) => addExercise(eq, name, movementId)}
            onRemove={removeExercise}
            onUpdateNote={updateExerciseNote}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Per-equipment accordion
// ─────────────────────────────────────────────

interface EquipmentAccordionProps {
  equipment: EquipmentType;
  label: string;
  exerciseItems: { exercise: ExerciseEntry; globalIndex: number }[];
  allMovements: Movement[];
  onAdd: (name: string, movementId?: string) => void;
  onRemove: (globalIndex: number) => void;
  onUpdateNote: (globalIndex: number, notes: string) => void;
}

function EquipmentAccordion({
  equipment,
  label,
  exerciseItems,
  allMovements,
  onAdd,
  onRemove,
  onUpdateNote,
}: EquipmentAccordionProps) {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [expandedNoteIndex, setExpandedNoteIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Equipment-specific frequent movements
  const frequentMovements = EQUIPMENT_MOVEMENTS[equipment] ?? [];
  const filteredFrequent = inputValue.trim()
    ? frequentMovements.filter((name) =>
        name.toLowerCase().includes(inputValue.toLowerCase().trim()),
      )
    : frequentMovements;

  // Catalog movements filtered by this equipment's category
  const filteredCatalog = inputValue.trim()
    ? allMovements.filter(
        (m) =>
          m.name.toLowerCase().includes(inputValue.toLowerCase().trim()) &&
          !frequentMovements.includes(m.name) &&
          (m.category === equipment || m.category === "other"),
      )
    : [];

  const hasDropdownContent = filteredFrequent.length > 0 || filteredCatalog.length > 0;

  function handleAdd(name: string, movementId?: string) {
    onAdd(name, movementId);
    setInputValue("");
    setShowDropdown(false);
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      handleAdd(inputValue.trim());
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg">
      {/* Header */}
      <div className="bg-gray-50 px-3 py-2 flex items-center justify-between rounded-t-lg">
        <span className="text-sm font-medium text-gray-700">
          {label}
          <span className="ml-1 text-xs text-gray-400">({exerciseItems.length})</span>
        </span>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Added exercises */}
        {exerciseItems.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {exerciseItems.map(({ exercise, globalIndex }) => (
              <div key={globalIndex} className="flex flex-col">
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border bg-primary-50 border-primary-200 text-primary-800">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedNoteIndex(expandedNoteIndex === globalIndex ? null : globalIndex)
                    }
                    className="hover:underline text-left"
                  >
                    {exercise.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemove(globalIndex)}
                    className="ml-1 text-primary-400 hover:text-primary-700 leading-none"
                    aria-label="제거"
                  >
                    ✕
                  </button>
                </div>
                {expandedNoteIndex === globalIndex && (
                  <input
                    type="text"
                    placeholder="동작 메모 (선택)"
                    value={exercise.notes ?? ""}
                    onChange={(e) => onUpdateNote(globalIndex, e.target.value)}
                    className="mt-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-primary-400"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="relative flex gap-2">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onKeyDown={handleKeyDown}
              placeholder={`${label} 동작 입력 또는 검색...`}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400"
            />

            {showDropdown && hasDropdownContent && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-10 max-h-48 overflow-y-auto"
              >
                {filteredFrequent.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-xs text-gray-400 font-medium bg-gray-50 sticky top-0">
                      빈출 동작
                    </div>
                    {filteredFrequent.map((name) => (
                      <button
                        key={name}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleAdd(name);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 text-gray-800"
                      >
                        {name}
                      </button>
                    ))}
                  </>
                )}
                {filteredCatalog.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-xs text-gray-400 font-medium bg-gray-50 sticky top-0">
                      카탈로그
                    </div>
                    {filteredCatalog.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleAdd(m.name, m.id);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 text-gray-800"
                      >
                        {m.name}
                        <span className="text-xs text-gray-400 ml-2">{m.category}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              if (inputValue.trim()) handleAdd(inputValue.trim());
            }}
            disabled={!inputValue.trim()}
            className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded disabled:opacity-40 hover:bg-primary-700 transition-colors"
          >
            추가
          </button>
        </div>
      </div>
    </div>
  );
}
